<template>
    <header
        ref="logobar"
        class="logobar"
        :class="[checkFixed, { 'logobar--hidden': logobarScrolled }, checkDevice]"
        :style="{...generateStylingVariables(generalStyleVariablePath)}"
    >
        <div
            ref="logobarInner"
            class="logobar__inner"
            :class="[checkAlignment, checkCtaLogoAlignment, checkLogobarType]"
        >
            <div class="logobar__main">
                <zoe-atom-media
                    :item="content.logo.logo.value"
                    :class="['logobar__logo', checkAlternativeLogoSize]"
                    title="Logo"
                    @load="mediaLoaded = true"
                    @click="handleLogoClick"
                />

                <zoe-atom-text
                    v-if="stringIsNotEmpty(content.text.value)"
                    :item="content.text.value"
                    class="logobar__text"
                    :class="checkTextPosition"
                />
            </div>

            <div
                v-if="logobarType === 'coBrand'"
                class="logobar__co-brand"
            >
                <zoe-atom-text
                    v-if="stringIsNotEmpty(content.coBrand.text.value)"
                    :item="content.coBrand.text.value"
                    class="logobar__co-brand-text"
                />

                <zoe-atom-media
                    :item="content.coBrand.logo.value"
                    class="logobar__co-brand-logo"
                    title="Co Brand logo"
                />
            </div>

            <zoe-molecule-review-company
                v-if="logobarType === 'reviewCompany'"
                :content="content.reviewCompany"
            />

            <div
                v-if="logobarType === 'ctaButton'"
                class="logobar__button-wrapper"
                :class="{ 'cta-bottom': checkCtaBottom }"
            >
                <button
                    ref="logobarButton"
                    class="button button--medium"
                    type="button"
                    @click="handleButton"
                >
                    <zoe-atom-text
                        is="span"
                        class="label"
                        :item="content.ctaButton.text.value"
                    />
                </button>
            </div>
        </div>
    </header>

    <div
        v-if="content.logobarType.value === 'ctaButton' && checkCtaBottom"
        ref="logobarBottom"
        class="logobar__cta-bottom"
        :class="{ 'cta--hidden': ctaScrolled }"
        :style="{...generateStylingVariables(generalStyleVariablePath)}"
    >
        <button
            class="button button--medium"
            type="button"
            @click="handleButton"
        >
            <zoe-atom-text
                is="span"
                class="label"
                :item="content.ctaButton.text.value"
            />
        </button>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { useStore } from 'vuex';
import { componentProps } from '../../../../mixins/component/component';
import { stringIsNotEmpty } from '../../../../../../resources/frontend/js/mixins/get-page-data';
import { goToNextStep } from '../../../../mixins/step/step';
import { generateStylingVariables } from '../../../../mixins/styling/styling';
import { isDesktopListener } from '../../../../../../resources/frontend/js/mixins/is-desktop-listener';
import getCurrentStep from '../../../../mixins/utils/get-current-step';
import ZoeAtomMedia from '../../../atoms/media/v1.0.0/zoe-atom-media.vue';
import ZoeAtomText from '../../../atoms/text/v1.0.0/zoe-atom-text.vue';
import ZoeMoleculeReviewCompany from '../../../molecules/review-company/v1.0.0/zoe-molecule-review-company.vue';
import scrollToSelector from '../../../../mixins/utils/scroll-to-selector';
import convertSize from '../../../../mixins/utils/convert-size';

const props = defineProps({
    content: componentProps.content,
});

const logobar = ref(null);
const logobarInner = ref(null);
const logobarBottom = ref(null);
const logobarButton = ref(null);
const logobarScrolled = ref(false);
const ctaScrolled = ref(false);
const logobarType = computed(() => props.content.logobarType.value);
const showOnDevice = computed(() => props.content.showOnDevice.value);
const mediaLoaded = ref(false);
let lastScrollPosition = 0;

const store = useStore();

watch(mediaLoaded, async value => {
    if (value) {
        setLogobarHeightInStore();
        checkButtonHeight();
    }
});

watch(() => props.content.logobarType.value, () => {
    checkButtonShadow();
});

const generalStyleVariablePath = computed(() => {
    if (props.content.style.button) {
        return props.content.style;
    }

    return props.content.ctaButton.style;
});

const checkFixed = computed(() => {
    if (props.content.position.value === 'fixed' || props.content.position.value === 'sticky') {
        return 'logobar--fixed';
    }
    return '';
});

const checkDevice = computed(() => {
    switch (showOnDevice.value) {
        case 'desktop':
            return 'logobar--desktop';
        case 'mobile':
            return 'logobar--mobile';
        default:
            return '';
    }
});

const checkTextPosition = computed(() => {
    return props.content.textAboveMedia.value ? 'logobar__text--above' : '';
});

const checkAlignment = computed(() => {
    return `logobar__inner--${props.content.single.logoAlignment.value}`;
});

const checkCtaLogoAlignment = computed(() => {
    if (props.content.logobarType.value === 'ctaButton') {
        if (props.content.ctaButton.position.value === 'fixedBottom' || props.content.ctaButton.position.value === 'stickyBottom') {
            return `logobar__cta-button--${props.content.ctaButton.logoAlignment.value}`;
        }
    }
    return '';
});

const checkLogobarType = computed(() => {
    return `logobar--${props.content.logobarType.value}`;
});

const checkCtaBottom = computed(() => {
    return props.content.ctaButton.position.value === 'fixedBottom' || props.content.ctaButton.position.value === 'stickyBottom';
});

const checkAlternativeLogoSize = computed(() => {
    return props.content.logo.alternativeLogoSize.value ? 'logobar__logo--alternative-size' : '';
});

const deviceType = computed(() => {
    return store.state.page.deviceType;
});

const handleScroll = () => {
    const currentScrollPosition = window.scrollY;
    const scrollThreshold = 10;

    const logobarSticky = props.content.position.value === 'sticky';
    const ctaStickyBottom = props.content.ctaButton.position.value === 'stickyBottom';

    if (currentScrollPosition > lastScrollPosition + scrollThreshold) {
        logobarScrolled.value = logobarSticky ? true : logobarScrolled.value;
        ctaScrolled.value = ctaStickyBottom ? true : ctaScrolled.value;
    } else if (currentScrollPosition < lastScrollPosition - scrollThreshold) {
        logobarScrolled.value = logobarSticky ? false : logobarScrolled.value;
        ctaScrolled.value = ctaStickyBottom ? false : ctaScrolled.value;
    }

    lastScrollPosition = currentScrollPosition;
};

const addShadowClass = button => {
    if (button) {
        const shadow = window.getComputedStyle(button).boxShadow;
        const regex = /\b0px\s+0px\s+0px\s+0px\b/g;
        const matches = shadow.match(regex);

        if (!matches) {
            button.classList.add('logobar__button--shadow');
        }
    }
};

const checkButtonShadow = () => {
    if (props.content.logobarType.value === 'ctaButton') {
        nextTick(() => {
            const ctaPosition = props.content.ctaButton.position.value;
            let button;

            const desktopWindow = window.innerWidth > 1024;

            if (ctaPosition === 'inLogobar' || desktopWindow) {
                button = logobarInner.value.querySelector('.logobar__button');
            } else if (ctaPosition === 'fixedBottom' || ctaPosition === 'stickyBottom') {
                button = logobarBottom.value.querySelector('.logobar__button');
            }

            addShadowClass(button);
        });
    }
};

const setLogobarHeightInStore = () => {
    store.commit('component/setOrganismValue', {
        component: 'logobar',
        object: { height: logobar.value.clientHeight },
    });
};

const handleButton = () => {
    const startCtaIsActive = store.getters['component/getOrganismValue']('startCta', 'isActive');
    const currentStep = getCurrentStep();

    if (startCtaIsActive && currentStep.type === 'start' && store.state.page.steps.length === 1) {
        scrollToSelector(store.state.page.scrollTarget, -40, () => {
            goToNextStep();

            store.commit('component/setOrganismValue', {
                component: 'startCta',
                object: { isActive: false },
            });

            store.commit('component/setOrganismValue', {
                component: 'logobar',
                object: { actionClickButton: true },
            });
        });

        return;
    }

    if (startCtaIsActive && currentStep.type === 'start') {
        scrollToSelector(store.state.page.scrollTarget, -40, () => {
            goToNextStep();
        });
    }

    if (!startCtaIsActive && currentStep.type === 'start') {
        scrollToSelector('.questions, .form', '-40');
    }

    if (currentStep.type === 'quiz') {
        scrollToSelector('.questions', '-40');
    }

    if (currentStep.type === 'form') {
        scrollToSelector('.form', '-40');
    }

    store.commit('component/setOrganismValue', {
        component: 'logobar',
        object: { actionClickButton: true },
    });
};

const handleLogoClick = () => {
    if (props.content.alertDialog.enable.value && !props.content.logo.scrollToActionPoint.value) {
        store.state.component.organism.alertDialog.show = true;
        return;
    }

    if (!props.content.logo.scrollToActionPoint.value) return;

    const startCtaIsActive = store.getters['component/getOrganismValue']('startCta', 'isActive');
    const currentStep = getCurrentStep();
    const offset = convertSize(-40, true);

    if (deviceType.value === 'mobile') {
        if (currentStep.type === 'start') {
            if (startCtaIsActive) {
                scrollToSelector('.start-cta', offset);
            } else {
                scrollToSelector('.questions', offset);
            }
        }

        if (currentStep.type === 'form') {
            scrollToSelector('.form', offset);
        }
    }

    store.commit('component/setOrganismValue', {
        component: 'logobar',
        object: { actionClickLogo: true },
    });
};

const getDeepestChild = element => {
    if (element.lastElementChild) {
        const child = getDeepestChild(element.lastElementChild);
        // Check if the deepest child is a text styling element
        if (['B', 'I', 'EM', 'STRONG', 'SPAN', 'S', 'U'].includes(child.tagName)) {
            // If it is, return the parent of that element
            return element;
        } else {
            return child;
        }
    } else {
        return element;
    }
};

const checkButtonHeight = () => {
    if (logobarButton.value) {
        const element = logobarButton.value;
        const textWrapper = element.querySelector('.label');
        const text = getDeepestChild(element);

        const textHeight = text.getBoundingClientRect().height;
        const textStyle = window.getComputedStyle(text);
        const lineHeight = parseFloat(textStyle.lineHeight);

        const hasConditionalText = Array.from(textWrapper.children).some(child => child.className.includes('conditional'));

        if (textHeight > lineHeight || (textWrapper.children.length > 1 || hasConditionalText)) {
            element.classList.add('logobar__button--multiline');
        }
    }
};

onMounted(() => {
    store.commit('component/setOrganismValue', {
        component: 'logobar',
        object: { position: props.content.position.value },
    });

    checkButtonShadow();

    if (props.content.position.value === 'sticky' || props.content.ctaButton.position.value === 'stickyBottom') {
        window.addEventListener('scroll', handleScroll);
    }

    nextTick(() => {
        isDesktopListener(store.state.page.step, setLogobarHeightInStore);
    });
});

onUnmounted(() => {
    window.removeEventListener('scroll', handleScroll);
});

</script>

<style lang="scss" scoped>
@use 'zoe-organism-logobar';

@layer organism {
    .logobar, .logobar__cta-bottom{
        --background: v-bind('content.style.background.value');
    }
}
</style>
